import type { AbstractIntlMessages } from 'next-intl'

const GLOBAL_MESSAGE_NAMESPACES = [
  'AdminImpersonate',
  'AIReply',
  'AiAssistant',
  'AgentExpanded',
  'AgentSources',
  'Auth',
  'ChunkLoadRecovery',
  'Common',
  'Contacts',
  'CreateWorkspace',
  'Errors',
  'FeedbackHub',
  'FeatureRequests',
  'Invite',
  'InboxAgentFollowUp',
  'InboxMarkDone',
  'InboxScheduledMessage',
  'LanguagePopup',
  'Manual',
  'NotFound',
  'Notifications',
  'Offline',
  'Onboarding',
  'PageNotFound',
  'PageTitles',
  'permissions',
  'Phone',
  'PublicForm',
  'Settings',
  'SharedClaim',
  'Sidebar',
  'Toast',
  'VendorQuote',
  'WorkspaceSwitcher',
  'Workforce',
  'WebsiteWidget',
] as const

export const ROOT_MESSAGE_NAMESPACES = GLOBAL_MESSAGE_NAMESPACES

/**
 * What a page served to someone who is not signed in may load.
 *
 * The global set exists for the authenticated shell and carries `Settings`
 * alone at 282 KB, plus `Workforce`, `Contacts`, `Onboarding` and the rest of
 * the staff surface. A guest opening a portal to type their passport number
 * was downloading the team-scheduling strings, which is roughly half the bytes
 * of that page and buys the guest nothing.
 *
 * This is deliberately a list and not a subtraction: a namespace added to the
 * signed-in shell later must not silently reach a public page. Anything a
 * public page genuinely needs is added here on purpose.
 *
 * Only chrome belongs here; a public surface adds its own namespaces through
 * its section. The sign-in pages load this set too, and Next embeds every
 * cookie-resolved server prop twice in the HTML (the page payload plus the
 * Partial Prefetching copy), so each namespace listed here costs every
 * signed-out first load twice.
 */
export const PUBLIC_MESSAGE_NAMESPACES = [
  // Chrome that can render on any page: errors, toasts, offline and the
  // chunk-load recovery boundary.
  'ChunkLoadRecovery',
  'Common',
  'Errors',
  'LanguagePopup',
  'NotFound',
  'Offline',
  'PageNotFound',
  'Toast',
] as const

// The inbox, and the sections that open its conversations next to their own content.
const INBOX_MESSAGE_NAMESPACES = [
  'Labs',
  'LabsSuggestions',
  'Agent',
  'AgentTools',
  'AiAssistant',
  // Airbnb payouts in the Booking tab of Airbnb-direct reservations.
  'AirbnbTransactions',
  // Airbnb special offers, price quotes and the host's change requests in the Booking tab.
  'AirbnbOffers',
  'AirbnbAlterationProposal',
  // An admin's cancellation of a confirmed Airbnb-direct reservation in the Booking tab.
  'AirbnbReservationCancellation',
  // The door code Airbnb gives the guest of an Airbnb-direct reservation (Seamless Entry API).
  'AirbnbSeamlessEntry',
  'ChatPage',
  'Claims',
  'Comments',
  'ConversationActivity',
  'DetailSidebar',
  'FollowupPicker',
  'Inbox',
  'InboxChannels',
  'InboxSidebar',
  // The chip that narrows a list view to a chat result it was expanded from.
  'ListScope',
  'Copilot',
  'Dashboard',
  'NewMessageDialog',
  'Properties',
  'PropertiesCrm',
  'QuickAssign',
  'Tasks',
  'CleaningCalculator',
  'TaskCleaningCalculator',
  'TaskCostSettings',
  'ToolCallLabels',
  'UnmappedContact',
] as const

export const SECTION_MESSAGE_NAMESPACES = {
  // TaskAutomation: the Escalation flow page reuses the Core Workflows editor.
  'ai-tickets': ['AiTickets', 'TaskAutomation', ...INBOX_MESSAGE_NAMESPACES],
  accounting: ['Accounting'],
  admin: ['AdminAgentRuns', 'AdminBilling'],
  // Sign-in, sign-up, password reset and set-password, loaded with the public
  // set: these pages are served signed out and never render the staff shell.
  auth: ['Auth', 'PageTitles'],
  workspace: [
    'AccessInstructions',
    // Property defaults and task sidebars share the Breezeway repair action.
    'BreezewayAssignment',
    'Agent',
    'AgentConnectionNotice',
    'AgentArtifacts',
    'AgentTools',
    // Loaded at workspace scope so workflow trigger detail UI opened from
    // shared shells can resolve `useTranslations('Automations.Options')`
    // without throwing MISSING_MESSAGE (PROD-5722).
    'Automations',
    // Loaded at workspace scope so the AI assistant sub-agent widget rendered
    // in the global right-dock can resolve `useTranslations('AIHub')` from any
    // section (property pages, tasks, etc.) (PROD-5196).
    'AIHub',
    'Artifacts',
    // Loaded at workspace scope so the canonical AgentWorkflowList embedded in
    // the global right-dock (Insights → Agents → Workflows drill) can resolve
    // `useTranslations('AgentWorkspace')` for its header/empty state (PROD-3767).
    'AgentWorkspace',
    'AiEntityList',
    'AiSchedule',
    // The host's Airbnb reviews of guests render in the Inbox Booking tab and on
    // the Reviews page, including its Insights embed.
    'AirbnbGuestReviews',
    // PROD-3731 — the BulkEditCoach modal is mounted in
    // ConditionalWorkspaceLayout (under this `workspace` section provider) so
    // it can trigger from any task surface that opens a right-dock sidebar.
    // Load its namespace here, same rationale as Insights/Reviews below.
    'BulkEditCoach',
    'Calendar',
    'ChatPage',
    'Claims',
    'Comments',
    'ConditionOptions',
    'CostItems',
    'Dashboard',
    'DepartmentNames',
    'DetailSidebar',
    'EditorAI',
    // Loaded at workspace scope so the property ElementDetailPanel, which opens
    // into the global right-dock (mounted in ConditionalWorkspaceLayout, outside
    // the `properties` section provider), can resolve `useTranslations('Elements')`
    // for its field labels instead of rendering raw keys. Same rationale as the
    // Rooms namespace below (PROD-5224).
    'Elements',
    'FollowupPicker',
    // Shared template and payroll editors can open from any workspace section.
    'FormulaBuilder',
    // Reservation details can open in the global right-dock from any workspace
    // route, so the native portal status card must be available at this scope.
    'GuestBlacklist',
    'GuestPortal',
    'GuestServiceCart',
    'HelpCenter',
    'Inbox',
    // Approval cards render in every AI Chat surface, including the right-dock chat on any route.
    'ApprovalCard',
    // Escalation and learned cards render wherever an Inbox conversation thread does.
    'EscalationCard',
    // With AI Tickets on, the escalation card shows its ticket (ticket-card-face.tsx).
    'AiTickets',
    // Loaded at workspace scope so the account-settings popover reached from
    // the NavRail (rendered under this `workspace` section provider on every
    // route) can resolve `useTranslations('InboxSidebar')` in its inbox-view
    // notification subscriptions section instead of throwing MISSING_MESSAGE.
    'InboxSidebar',
    'InspectionReview',
    // Loaded at workspace scope so call-flow right-dock panels opened from
    // settings can resolve `useTranslations('KnowledgeBase')` for shared AI
    // voice agent fields rendered by PhoneSection.
    'KnowledgeBase',
    'Monitoring',
    // Loaded at workspace scope so review-detail content rendered in the
    // global right-dock can resolve `useTranslations('Insights')` (used for
    // reservation/task/visit field labels).
    'Insights',
    'LocationConsent',
    'LocationTrackingSelfView',
    'OwnerPortal',
    // Policy editors render in the global right dock, so keep their labels
    // available even when the active section changes while a pane is open.
    'Policies',
    'Properties',
    'PropertiesCrm',
    // Loaded at workspace scope so task/visit/contact detail sidebars opened
    // from shared shells (agent chat detail preview, calendar, artifacts) can
    // resolve `useTranslations('PropertyDetail')` via activity log and
    // editable custom fields instead of throwing MISSING_MESSAGE.
    'PropertyDetail',
    'PropertyViews',
    // Loaded at the workspace scope so the global right-dock (mounted in
    // ConditionalWorkspaceLayout, outside any per-section intl provider) can
    // resolve `useTranslations('Reviews')` from any review detail / drilldown
    // sidebar that opens into the dock from the insights or reviews pages.
    'Reviews',
    // Loaded at workspace scope so the property RoomDetailPanel, which opens
    // into the global right-dock (mounted in ConditionalWorkspaceLayout,
    // outside the `properties` section provider), can resolve
    // `useTranslations('Rooms')` instead of throwing MISSING_MESSAGE (PROD-5224).
    'Rooms',
    'RoomTypes',
    // AgentScheduleWidget is available from the global assistant on every
    // workspace route, and its property map resolves Scheduling labels.
    'Scheduling',
    'ShiftClockMenu',
    // Completed-task PDFs reuse the shared report from any task detail dock.
    'SharedTask',
    'SidebarBreadcrumb',
    'Supplies',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'ToolCallLabels',
    'Users',
    'VideoSteps',
    'VisitCosts',
    'VisitPrices',
    'Visits',
    'WhatsAppTemplateDialog',
    'Workflows',
    'WorkerView',
  ],
  agent: [
    'Agent',
    'AgentExpanded',
    'AIHub',
    'AiAssistant',
    'ChatPage',
    'KnowledgeBase',
    'ToolCallLabels',
  ],
  agentChat: [
    'Agent',
    'AgentArtifacts',
    'AgentConnectionNotice',
    'AgentTools',
    'AgentWorkspace',
    'AIHub',
    'AiAssistant',
    'ChatPage',
    'Claims',
    'Inbox',
    'KnowledgeBase',
    'Properties',
    'PropertiesCrm',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'ToolCallLabels',
    'Users',
    'Workflows',
  ],
  automations: [
    'Agent',
    'AiAssistant',
    'Automations',
    'Inbox',
    'Scheduling',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'ToolCallLabels',
    'Upsells',
    'Views',
    'Workflows',
    'templates',
  ],
  /** The Workflow V2 editor's catalog block forms, on top of automations. */
  workflowEditor: ['WorkflowBlocks'],
  calendar: [
    'Calendar',
    'CalendarDayView',
    'Scheduling',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'TasksSidebar',
    'Views',
  ],
  contacts: ['ContactInfo', 'Contacts', 'Inbox', 'Views'],
  // Internal /design component gallery renders real shipped components
  // (e.g. TaskStatusTabs and agent-workspace specimens) that resolve product
  // namespaces at runtime.
  design: ['AgentWorkspace', 'Insights', 'Monitoring', 'Tasks'],
  'event-logs': ['EventLogs'],
  inbox: INBOX_MESSAGE_NAMESPACES,
  insights: [],
  kb: [
    'VoiceCapabilities',
    'Agent',
    'AgentWorkspace',
    'AIHub',
    'Automations',
    'GoogleWorkspaceCompliance',
    'ImportHub',
    'Inbox',
    'InboxSidebar',
    'KB',
    'KnowledgeBase',
    'Learning',
    'Meetings',
    // Loaded at kb scope so the Learning stage can render the property Details
    // section through FieldSectionRenderer and PropertyOverviewHero.
    'Properties',
    'PropertyDetail',
    'SharedScratchpad',
    'Teachings',
    'ToolChip',
  ],
  manuals: ['Dashboard', 'HouseManualBlocks', 'HouseManuals', 'Properties'],
  knowledge: [
    'Labs',
    'LabsSuggestions',
    'Agent',
    'AgentWorkspace',
    'AIHub',
    'Automations',
    'GoogleWorkspaceCompliance',
    'ImportHub',
    'Inbox',
    'InboxSidebar',
    'KB',
    'KnowledgeBase',
    'KnowledgeNavigation',
    'Learning',
    'Meetings',
    'Policies',
    'Properties',
    'PropertyDetail',
    'SharedScratchpad',
    'Teachings',
    'ToolChip',
  ],
  monitoring: [
    'AgentWorkspace',
    'Automations',
    // Artifacts + PropertyDetail: the monitoring surface renders agent chat
    // detail previews whose sidebars reach these namespaces (same chain as
    // the workspace-scope PropertyDetail entry).
    'Artifacts',
    // Calendar: the reservation detail sidebar renders nights/guest counts
    // from the Calendar ICU keys.
    'Calendar',
    'ChatPage',
    'Claims',
    'Comments',
    'ConditionOptions',
    'CostItems',
    'DepartmentNames',
    'DetailSidebar',
    'EditorAI',
    'FollowupPicker',
    'GuestBlacklist',
    'GuestPortal',
    'GuestServiceCart',
    'Inbox',
    'Insights',
    'InspectionReview',
    'KnowledgeBase',
    'Monitoring',
    'OwnerPortal',
    'Properties',
    'PropertiesCrm',
    'PropertyDetail',
    'Reviews',
    'RoomTypes',
    'SidebarBreadcrumb',
    'Supplies',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'ToolCallLabels',
    'Users',
    'VisitCosts',
    'VisitPrices',
    'Visits',
    'Workflows',
    'WorkerView',
  ],
  properties: [
    'AccessInstructions',
    'CleaningCalculator',
    'Calendar',
    // CalendarDayView/TasksSidebar/Views: the property detail page embeds the
    // reservation/task calendar day view and task sidebar surfaces that read
    // these namespaces.
    'CalendarDayView',
    'Claims',
    'Contacts',
    'CostItems',
    'Dashboard',
    'Elements',
    'HouseManualBlocks',
    'HouseManuals',
    'Inbox',
    'Manual',
    'OwnerPortal',
    // The reservations section mounts this provider, so its portal guide
    // catalogue resolves here rather than under a guest-portal-only bundle.
    'PortalGuides',
    // Same reason: the guest portal builder lives under this provider and
    // previews the guest registration form, whose field placeholders resolve
    // in PublicForm. Without it the preview shows the raw key.
    'PublicForm',
    // The property detail Policies section and its right-dock editors resolve
    // shared policy actions and status labels from this namespace.
    'Policies',
    'Properties',
    'PropertiesCrm',
    'PropertyBulkEdit',
    'PropertyDetail',
    'PropertyDocuments',
    'PropertyManuals',
    // PropertyGroupDetail: the property-group detail page client reads its
    // header/labels from this namespace.
    'PropertyGroupDetail',
    'GroupTemplateRules',
    'PropertyGallery',
    'PropertyInbox',
    'PropertyTeam',
    'RoomTypes',
    'Rooms',
    'Scheduling',
    // The Templates section lists the automations covering the property.
    'TaskAutomation',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'TasksSidebar',
    'Views',
  ],
  reviews: ['Insights', 'Reviews'],
  settings: [
    // The connected Airbnb host's profile, restrictions and connection check.
    'AirbnbAccount',
    // The host's Airbnb notifications on the account card.
    'AirbnbNotifications',
    // A mapped listing's quality state, reservation issues and content review.
    'AirbnbListingQuality',
    // Airbnb's amenity catalog labels, kept out of the global `Settings` payload.
    'AirbnbAmenities',
    // Airbnb listing regulations and their registration forms.
    'AirbnbPermits',
    'AirbnbTransactions',
    // Airbnb discounts and seasonal rule groups in the listing settings.
    'AirbnbPricingRules',
    // Airbnb pass-through occupancy taxes in the listing settings.
    'AirbnbPassThroughTaxes',
    // Airbnb listing photos and rooms.
    'AirbnbMedia',
    // The New Listing promotion of each mapped Airbnb listing.
    'AirbnbPromotions',
    // The opportunities Airbnb offers for each mapped listing.
    'AirbnbOpportunities',
    // The Airbnb check-in guide of a listing and its steps.
    'AirbnbCheckInGuide',
    // A listing's smart lock and backup instructions on Airbnb (Seamless Entry API).
    'AirbnbSeamlessEntry',
    'InboxRouting',
    'ChatPage',
    'DetailSidebar',
    'SidebarBreadcrumb',
    'remotelock',
    'nuki',
    // Connected Amazon Business accounts and their markets.
    'AmazonBusiness',
    'Compliance',
    'GoogleWorkspaceCompliance',
    'GoogleSheets',
    'Inbox',
    'InboxSidebar',
    'InviteManager',
    'KnowledgeBase',
    'Properties',
    'QuoSection',
    'Settings',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'Workflows',
    'WorkforceSettings',
  ],
  ownerPortal: [
    'OwnerPortal',
    'Calendar',
    'Claims',
    // Shared reservation detail summary labels (check-in, check-out, guests).
    'ChatPage',
    'Common',
    'ConditionOptions',
    'DetailSidebar',
    // Shared reservation calendar and detail components resolve their status
    // labels from this namespace in both the app and the public owner portal.
    'PropertiesCrm',
    'SidebarBreadcrumb',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
  ],
  // PublicForm: the builder previews the guest registration form, which
  // resolves its field placeholders there. Without it the preview shows the
  // raw key instead of the label. PortalGuides and Workflows are resolved
  // directly by the guest surfaces.
  guestPortal: [
    'AccessInstructions',
    'GuestPortal',
    'GuestServiceCart',
    'GuestUpsell',
    'PortalGuides',
    'PublicForm',
    'Workflows',
  ],
  // The public claim page renders the shared AttachmentPreviewDialog for its
  // evidence gallery, which resolves its labels from `Tasks` (PROD-8655).
  sharedClaim: ['SharedClaim', 'Tasks'],
  sharedTask: ['CostItems', 'HouseManualBlocks', 'HouseManuals', 'SharedTask', 'Tasks'],
  sharedVisit: [
    'SharedVisit',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'ConditionOptions',
    'Common',
  ],
  supplies: ['Supplies', 'SuppliesSidebar', 'Supply'],
  tasks: [
    'AutoScheduleSettings',
    'Calendar',
    'CalendarDayView',
    'Claims',
    'CostItems',
    'DetailSidebar',
    // The automation scope picker reuses the guest-portal property group picker.
    'GuestPortal',
    'Properties',
    'Scheduling',
    'SuppliesSidebar',
    'TaskAutomation',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'TasksSidebar',
    'Views',
    'taskClosureRules',
    'ListScope',
  ],
  workforce: [
    'Calendar',
    'LocationConsent',
    'LocationTrackingSelfView',
    'Dashboard',
    'Insights',
    'InviteManager',
    'Scheduling',
    'Tasks',
    'CleaningCalculator',
    'TaskCleaningCalculator',
    'TaskCostSettings',
    'Views',
    'Visits',
    'WorkerView',
    'Workforce',
    'WorkforceFinancial',
  ],
} as const satisfies Record<string, readonly string[]>

export type ClientMessageSection = keyof typeof SECTION_MESSAGE_NAMESPACES

function isMessageObject(value: unknown): value is AbstractIntlMessages {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isMessageValue(value: unknown): value is AbstractIntlMessages | string {
  return typeof value === 'string' || isMessageObject(value)
}

export function pickMessageNamespaces(
  messages: Record<string, unknown>,
  namespaces: readonly string[]
): AbstractIntlMessages {
  const picked: AbstractIntlMessages = {}
  const missing: string[] = []

  for (const namespace of namespaces) {
    const value = messages[namespace]
    if (isMessageValue(value)) {
      picked[namespace] = value
    } else {
      missing.push(namespace)
    }
  }

  if (missing.length > 0) {
    throw new Error(`Missing client message namespaces: ${missing.join(', ')}`)
  }

  return picked
}

// The root layout renders a static shell whose client-side provider carries
// only the overlay subset, so each top-level segment's provider serves the
// global namespaces server-resolved (includeGlobal) exactly once per subtree;
// nested section providers stay deltas and merge on top of it.
export function getSectionMessageNamespaces(
  section: ClientMessageSection | undefined,
  options?: { includeGlobal?: boolean; includePublic?: boolean }
): string[] {
  const sectionNamespaces = section ? SECTION_MESSAGE_NAMESPACES[section] : []
  if (options?.includePublic) {
    // `includePublic` wins over `includeGlobal`: a caller that asks for the
    // public set is saying this page is served to someone signed out, and
    // widening it back to the staff set would defeat the request.
    return Array.from(new Set<string>([...PUBLIC_MESSAGE_NAMESPACES, ...sectionNamespaces]))
  }
  if (options?.includeGlobal) {
    return Array.from(new Set<string>([...GLOBAL_MESSAGE_NAMESPACES, ...sectionNamespaces]))
  }
  const globalSet = new Set<string>(GLOBAL_MESSAGE_NAMESPACES)
  return Array.from(new Set(sectionNamespaces)).filter((namespace) => !globalSet.has(namespace))
}
